// 배너 신규/수정 공용 폼.
// link_kind 에 따라 입력 필드(linkTargetId / linkUrl) 가 동적으로 바뀐다.

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  BANNER_LINK_KIND,
  IMAGE_UPLOAD_KIND,
  type BannerLinkKind,
} from "@oo/shared";
import {
  createBanner,
  listBannersAdmin,
  listCategoriesAdmin,
  listProductsAdmin,
  updateBanner,
} from "@/features/catalog/catalogApi.ts";
import { Button } from "@oo/ui";
import { PageHeader } from "@/shared/ui";
import ImageUploader from "@/features/catalog/ImageUploader.tsx";

interface FormState {
  imageKey: string | null;
  linkKind: BannerLinkKind;
  linkTargetId: string;
  linkUrl: string;
  startsAt: string; // datetime-local
  endsAt: string;
  isActive: boolean;
}

const DEFAULT_STATE: FormState = {
  imageKey: null,
  linkKind: BANNER_LINK_KIND.NONE,
  linkTargetId: "",
  linkUrl: "",
  startsAt: "",
  endsAt: "",
  isActive: true,
};

// datetime-local <-> ISO 8601 변환.
// 브라우저 입력은 로컬 시간이지만 서버에는 UTC ISO 로 보낸다.
function localToIso(value: string): string | null {
  if (!value) return null;
  return new Date(value).toISOString();
}
function isoToLocal(value: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  // datetime-local 입력 포맷 (YYYY-MM-DDTHH:mm). 로컬 타임존 기준.
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// 배너 생성/수정 화면.
export default function BannerFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [state, setState] = useState<FormState>(DEFAULT_STATE);
  const [error, setError] = useState<string | null>(null);

  const { data: banners = [] } = useQuery({
    queryKey: ["admin", "banners"],
    queryFn: listBannersAdmin,
    enabled: !isNew,
  });

  // 연결 대상 선택용 목록. UUID 직접 입력 대신 드롭다운으로 고르게 한다(개선항목 1).
  const { data: categories = [] } = useQuery({
    queryKey: ["admin", "categories"],
    queryFn: listCategoriesAdmin,
  });
  // 상품은 페이지네이션이지만 배너 연결 선택지로는 전량이 필요하다.
  // 서버 limit 상한이 100 이므로(초과 시 400) 100 으로 요청한다. 단일 매장 규모라 충분하다.
  const { data: productList } = useQuery({
    queryKey: ["admin", "products", "for-banner"],
    queryFn: () => listProductsAdmin({ limit: 100, offset: 0 }),
  });
  const products = productList?.products ?? [];

  useEffect(() => {
    if (!isNew) {
      const existing = banners.find((b) => b.id === id);
      if (existing) {
        setState({
          imageKey: existing.imageKey,
          linkKind: existing.linkKind,
          linkTargetId: existing.linkTargetId ?? "",
          linkUrl: existing.linkUrl ?? "",
          startsAt: isoToLocal(existing.startsAt),
          endsAt: isoToLocal(existing.endsAt),
          isActive: existing.isActive,
        });
      }
    }
  }, [isNew, banners, id]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!state.imageKey) throw new Error("배너 이미지를 업로드하세요.");
      if (
        (state.linkKind === BANNER_LINK_KIND.PRODUCT || state.linkKind === BANNER_LINK_KIND.CATEGORY) &&
        !state.linkTargetId.trim()
      ) {
        throw new Error("연결 대상 ID 를 입력하세요.");
      }
      if (state.linkKind === BANNER_LINK_KIND.EXTERNAL && !state.linkUrl.trim()) {
        throw new Error("외부 링크 URL 을 입력하세요.");
      }
      const payload = {
        imageKey: state.imageKey,
        linkKind: state.linkKind,
        linkTargetId:
          state.linkKind === BANNER_LINK_KIND.PRODUCT || state.linkKind === BANNER_LINK_KIND.CATEGORY
            ? state.linkTargetId.trim()
            : null,
        linkUrl: state.linkKind === BANNER_LINK_KIND.EXTERNAL ? state.linkUrl.trim() : null,
        startsAt: localToIso(state.startsAt),
        endsAt: localToIso(state.endsAt),
        isActive: state.isActive,
      };
      if (isNew) {
        await createBanner(payload);
      } else {
        await updateBanner(id!, payload);
      }
    },
    onSuccess: () => navigate("/catalog/banners", { replace: true }),
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "저장 실패"),
  });

  return (
    <div className="mx-auto flex max-w-screen-md flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8">
      <PageHeader
        title={isNew ? "신규 배너 등록" : "배너 수정"}
        actions={
          <Button variant="secondary" size="sm" onClick={() => navigate("/catalog/banners")}>
            목록으로
          </Button>
        }
      />

      <section className="flex flex-col gap-3 rounded-md border border-ink-200 bg-white p-4">
        <h3 className="text-sm font-semibold">배너 이미지 (가로형 권장)</h3>
        <ImageUploader
          kind={IMAGE_UPLOAD_KIND.BANNER}
          value={state.imageKey}
          onChange={(key) => setState((s) => ({ ...s, imageKey: key }))}
          previewClassName="h-32 w-full"
        />
      </section>

      <section className="flex flex-col gap-3 rounded-md border border-ink-200 bg-white p-4">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-ink-600">클릭 시 동작</span>
          <select
            value={state.linkKind}
            onChange={(e) => setState((s) => ({ ...s, linkKind: e.target.value as BannerLinkKind }))}
            className="rounded-md border border-ink-300 px-3 py-2 text-sm"
          >
            <option value={BANNER_LINK_KIND.NONE}>없음 (클릭 무동작)</option>
            <option value={BANNER_LINK_KIND.PRODUCT}>상품 상세로 이동</option>
            <option value={BANNER_LINK_KIND.CATEGORY}>카테고리로 이동</option>
            <option value={BANNER_LINK_KIND.EXTERNAL}>외부 링크</option>
          </select>
        </label>
        {state.linkKind === BANNER_LINK_KIND.PRODUCT ? (
          <label className="flex flex-col gap-1">
            <span className="text-xs text-ink-600">연결할 상품</span>
            {/* UUID 직접 입력 대신 등록된 상품을 드롭다운으로 선택한다. */}
            <select
              value={state.linkTargetId}
              onChange={(e) => setState((s) => ({ ...s, linkTargetId: e.target.value }))}
              className="rounded-md border border-ink-300 px-3 py-2 text-sm"
            >
              <option value="">상품을 선택하세요</option>
              {/* 수정 진입 시 현재 연결된 상품이 목록에 없을 수도 있어(숨김/삭제 등) 안내 옵션을 덧붙인다. */}
              {state.linkTargetId && !products.some((p) => p.id === state.linkTargetId) ? (
                <option value={state.linkTargetId}>(현재 연결됨) {state.linkTargetId}</option>
              ) : null}
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {state.linkKind === BANNER_LINK_KIND.CATEGORY ? (
          <label className="flex flex-col gap-1">
            <span className="text-xs text-ink-600">연결할 카테고리</span>
            {/* UUID 직접 입력 대신 등록된 카테고리를 드롭다운으로 선택한다. */}
            <select
              value={state.linkTargetId}
              onChange={(e) => setState((s) => ({ ...s, linkTargetId: e.target.value }))}
              className="rounded-md border border-ink-300 px-3 py-2 text-sm"
            >
              <option value="">카테고리를 선택하세요</option>
              {state.linkTargetId && !categories.some((cat) => cat.id === state.linkTargetId) ? (
                <option value={state.linkTargetId}>(현재 연결됨) {state.linkTargetId}</option>
              ) : null}
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {state.linkKind === BANNER_LINK_KIND.EXTERNAL ? (
          <label className="flex flex-col gap-1">
            <span className="text-xs text-ink-600">외부 URL</span>
            <input
              type="url"
              value={state.linkUrl}
              onChange={(e) => setState((s) => ({ ...s, linkUrl: e.target.value }))}
              className="rounded-md border border-ink-300 px-3 py-2 text-sm"
              placeholder="https://..."
            />
          </label>
        ) : null}
      </section>

      <section className="grid grid-cols-2 gap-3 rounded-md border border-ink-200 bg-white p-4">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-ink-600">노출 시작 (선택)</span>
          <input
            type="datetime-local"
            value={state.startsAt}
            onChange={(e) => setState((s) => ({ ...s, startsAt: e.target.value }))}
            className="rounded-md border border-ink-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-ink-600">노출 종료 (선택)</span>
          <input
            type="datetime-local"
            value={state.endsAt}
            onChange={(e) => setState((s) => ({ ...s, endsAt: e.target.value }))}
            className="rounded-md border border-ink-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="col-span-2 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={state.isActive}
            onChange={(e) => setState((s) => ({ ...s, isActive: e.target.checked }))}
          />
          활성화 (사용자 화면에 노출)
        </label>
      </section>

      {error ? <p className="text-sm text-danger-600">{error}</p> : null}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => navigate("/catalog/banners")}
          className="rounded-md border border-ink-300 px-4 py-2 text-sm hover:bg-ink-100"
        >
          취소
        </button>
        <button
          type="button"
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {saveMut.isPending ? "저장 중..." : "저장"}
        </button>
      </div>
    </div>
  );
}
